import React, { useState, useEffect, useRef, useMemo } from 'react'
import { getCurrencySymbol } from '../utils/currencyUtils'
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Typography,
	Button,
	Box,
	Stack,
	Divider,
	useMediaQuery,
	useTheme,
	CircularProgress,
	IconButton,
	Drawer,
	Menu,
	MenuItem,
	ListItemIcon,
	ListItemText
} from '@mui/material'
import {
	Phone as PhoneIcon,
	Email as EmailIcon,
	Cancel as CancelIcon,
	PersonOff as NoShowIcon,
	CheckCircle as CheckOutIcon,
	MoreVert as MoreVertIcon,
	ArrowBack as ArrowBackIcon,
	Edit as EditIcon
} from '@mui/icons-material'
import { supabase } from "../utils/supabase"
import { getFullName } from '../utils/clientUtils'
import { STATUS_OPTIONS } from '../utils/statusUtils'
import { canEmployeePerformService, getAppointmentDuration } from '../utils/appointmentUtils'
import LogPaymentDialog from './LogPaymentDialog'
import { storedAddOnsTotal } from '../utils/checkoutUtils'
import { buildInvoiceView } from '../utils/invoiceUtils'
import { fetchPetAvatarsBatch } from '../utils/petPhotoUtils'
import { fetchMerchantLogo, getMerchantPhotoUrl } from '../utils/merchantPhotoUtils'
import { AppointmentsOverview, AddSingleAppointment } from './add-appointment'
import { L, Icon, LavMobileContext } from './add-appointment/appointmentLavenderFields'
import MobileAppointmentDetails from './appointment-details/MobileAppointmentDetails'
import DesktopAppointmentDetails from './appointment-details/DesktopAppointmentDetails'
import RescheduleFlow, { RescheduleOverlay } from './appointment-details/RescheduleFlow'
import RescheduleSlotPicker from './appointment-details/RescheduleSlotPicker'
import RescheduleConfirm from './appointment-details/RescheduleConfirm'
import AddEditClientDialog from './AddEditClientDialog'
import { buildMoveMessage, firstName as moveFirstName, groomerNames as moveGroomerNames, joinNames as movePetList, notifiedVerb, uniq as moveUniq } from './appointment-details/rescheduleShared'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone';
import { getAppointmentStatusDisplay } from '../utils/appointmentUtils';
import { COLOR_OPTIONS } from './ServiceEditorPanel'
import { getServicePrice } from '../utils/appointmentUtils'

dayjs.extend(utc)
dayjs.extend(timezone)


// The paid payments row doubles as the appointment's invoice (see
// invoiceUtils). Pull the joins buildInvoiceView reads so the completed
// appointment can open / download it without another round trip.
const PAID_RECORD_SELECT = `
	*,
	appointments (id, start_ts, end_ts, status, customer_name, user_id, group_id),
	clients (id, name, last_name, phone_number, email)
`

/**
 * @param {boolean} [initialReschedule] - Open straight into the reschedule
 *   slot picker once the appointment's data has loaded (the Bookings list's
 *   "Reschedule…" row action). Re-armed whenever a different appointment
 *   opens; the fast path itself is unchanged.
 */
function AppointmentDetailsDialog({ open, onClose, appointment: appointmentProp, merchant, onAppointmentUpdated, onOpenClientProfile, onOpenPetProfile, onOpenInvoice, initialReschedule = false }) {
	// Parents hand us the row from their list and refetch that list after a
	// change, but the object they passed never updates. Checkout changes the
	// row (status, add-ons, end time), so we overlay the fresh columns here
	// until the parent opens a different appointment.
	const [livePatch, setLivePatch] = useState(null)
	useEffect(() => { setLivePatch(null) }, [appointmentProp])
	const appointment = useMemo(
		() => (appointmentProp && livePatch ? { ...appointmentProp, ...livePatch } : appointmentProp),
		[appointmentProp, livePatch]
	)
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))

	// State for fetched data
	const [client, setClient] = useState(null)
	const [pet, setPet] = useState(null)
	const [service, setService] = useState(null)
	const [loading, setLoading] = useState(false)
	// Id of the appointment whose client / pet / service / group rows have
	// finished loading (null while fetching or closed). `loading` alone can't
	// tell "not started yet" from "done".
	const [loadedId, setLoadedId] = useState(null)
	const [currentStatus, setCurrentStatus] = useState('')
	const [updatingStatus, setUpdatingStatus] = useState(false)

	// Tapping the client or a pet leaves this appointment for that profile, so
	// the panel closes behind the navigation.
	const openClientProfile = onOpenClientProfile
		? (c) => { onClose(); onOpenClientProfile(c) }
		: undefined
	const openPetProfile = onOpenPetProfile
		? (entry) => { onClose(); onOpenPetProfile({ petId: entry.petId, petName: entry.petName, client }) }
		: undefined

	// Edit mode state
	const [editMode, setEditMode] = useState(false)
	const enteringEditRef = useRef(false)
	// Mirrors enteringEditRef for the UI: the Edit button shows a spinner while
	// the edit-mode data loads (the ref alone can't trigger a re-render).
	const [enteringEdit, setEnteringEdit] = useState(false)

	// Reschedule fast path (design "Reschedule · desktop / mobile"): the slot
	// picker slides over the details; confirming moves every row of the visit
	// by the same delta (never delete + recreate). `moved` keeps the
	// moved-from evidence bar until another appointment opens.
	const [rescheduleOpen, setRescheduleOpen] = useState(false)
	// The confirm step's contact lines open the Edit client popup in place
	// (never the profile, which would abandon the reschedule); saving refreshes
	// `client` so the notify card shows the new phone / email at once.
	const [editClientOpen, setEditClientOpen] = useState(false)
	const [rescheduling, setRescheduling] = useState(false)
	const [rescheduleEmployees, setRescheduleEmployees] = useState([])
	const [loadingRescheduleEmployees, setLoadingRescheduleEmployees] = useState(false)
	const [moved, setMoved] = useState(null) // { fromStartTs, notified }
	const [toast, setToast] = useState(null)
	// Edit flow (design "Edit appointment"): the When field opens the same slot
	// picker as a "Change date & time" overlay, and Save runs the move-confirm
	// step whenever the start time moved — the rows are then updated in place
	// by AppointmentsOverview and the client is notified afterwards.
	const [editSlotOpen, setEditSlotOpen] = useState(false)
	const [editPick, setEditPick] = useState(null)               // last pick from the picker (carries its verdict)
	const [editMoveConfirm, setEditMoveConfirm] = useState(null) // { date, time, groomer, empIds, check } awaiting confirm
	const [editNotify, setEditNotify] = useState(true)
	const [editSaving, setEditSaving] = useState(false)
	const editOriginalRef = useRef(null)                         // { date, time, startTs, empIds } when edit mode opened
	const editMoveRef = useRef(null)                             // { sent, date, time, previousStartTs } → notify after save
	const toastTimer = useRef(null)
	const ping = (msg) => {
		setToast(msg)
		clearTimeout(toastTimer.current)
		toastTimer.current = setTimeout(() => setToast(null), 2600)
	}
	useEffect(() => () => clearTimeout(toastTimer.current), [])
	useEffect(() => {
		setMoved(null)
		setRescheduleOpen(false)
		setToast(null)
	}, [appointmentProp?.id, open])
	const [allClients, setAllClients] = useState([])
	const [allEmployees, setAllEmployees] = useState([])
	const [allServices, setAllServices] = useState([])
	const [allAddOns, setAllAddOns] = useState([])
	const [clientPets, setClientPets] = useState([])
	const [selectedClient, setSelectedClient] = useState(null)
	const [selectedEmployee, setSelectedEmployee] = useState(null)
	const [selectedService, setSelectedService] = useState(null)
	const [selectedAddOns, setSelectedAddOns] = useState([])
	const [selectedDate, setSelectedDate] = useState('') // For rescheduling
	const [selectedTime, setSelectedTime] = useState('') // For rescheduling (HH:mm format)
	const [loadingPets, setLoadingPets] = useState(false)

	// Cancel choice dialog state
	const [showCancelChoiceDialog, setShowCancelChoiceDialog] = useState(false)
	const [isWithinCancelWindow, setIsWithinCancelWindow] = useState(false)

	// Payment dialog state
	const [showPaymentDialog, setShowPaymentDialog] = useState(false)

	// Payment details state
	const [showPaymentDetails, setShowPaymentDetails] = useState(false)
	const [paymentDetails, setPaymentDetails] = useState(null)
	const [loadingPayment, setLoadingPayment] = useState(false)
	// "Download PDF" from the completed appointment's invoice actions.
	const [downloadingInvoice, setDownloadingInvoice] = useState(false)
	// Whether a late-cancel fee can actually be charged: only online bookings
	// store a Stripe customer + payment method on the appointment. Merchant-
	// created appointments have neither, so "Cancel with fee" is hidden for
	// them. null = not yet known (treated as "can't charge").
	const [canChargeFee, setCanChargeFee] = useState(null)

	// More options menu state
	const [moreMenuAnchor, setMoreMenuAnchor] = useState(null)

	// Grouped appointments state
	const [groupedAppointments, setGroupedAppointments] = useState([])

	// Edit mode appointments for AppointmentsOverview
	const [editModeAppointments, setEditModeAppointments] = useState([])
	const [editModeDate, setEditModeDate] = useState('')
	const [editModeTime, setEditModeTime] = useState('')
	const [editModeStep, setEditModeStep] = useState('overview') // 'overview' | 'single'
	const [editingAppointmentIndex, setEditingAppointmentIndex] = useState(null)
	// Existing appointment rows the merchant removed in edit mode. Nothing is
	// deleted until Save so cancelling the edit leaves the booking untouched.
	const [editModeRemovedIds, setEditModeRemovedIds] = useState([])

	// Ref for mobile save action
	const singleAppointmentSaveRef = useRef(null)
	const overviewSaveRef = useRef(null)

	// Fetch client, pet, and service data when appointment changes
	useEffect(() => {
		const fetchAppointmentData = async () => {
			// Reset state first
			setLoadedId(null)
			setClient(null)
			setPet(null)
			setService(null)

			console.log('Fetching appointment details:', {
				user_id: appointment?.user_id,
				pet_id: appointment?.pet_id,
				service_id: appointment?.service_id,
				has_service_types: !!appointment?.service_types,
				service_types: appointment?.service_types,
				appointment: appointment
			})

			if (!appointment?.user_id && !appointment?.pet_id && !appointment?.service_id) {
				// No IDs to fetch
				console.log('No user_id, pet_id, or service_id found in appointment')
				setLoadedId(appointment?.id ?? null)
				return
			}

			setLoading(true)
			try {
				const promises = []

				// Fetch client data
				if (appointment.user_id) {
					const clientPromise = supabase
						.from('clients')
						.select('*')
						.eq('id', appointment.user_id)
						.single()
						.then(({ data, error }) => {
							if (error && error.code !== 'PGRST116') {
								console.error('Error fetching client:', error)
							} else if (data) {
								console.log('Client fetched successfully:', data)
								setClient(data)
							}
						})
					promises.push(clientPromise)
				}

				// Fetch pet data
				if (appointment.pet_id) {
					const petPromise = supabase
						.from('pets')
						.select('*')
						.eq('id', appointment.pet_id)
						.single()
						.then(({ data, error }) => {
							if (error && error.code !== 'PGRST116') {
								console.error('Error fetching pet:', error)
							} else if (data) {
								console.log('Pet fetched successfully:', data)
								setPet(data)
							}
						})
					promises.push(petPromise)
				}

				// Fetch service data
				if (appointment.service_id) {
					// Always try to fetch if service_id exists
					const servicePromise = supabase
						.from('service_types')
						.select('id, name, price, appointment_color, duration_minutes')
						.eq('id', appointment.service_id)
						.single()
						.then(({ data, error }) => {
							if (error && error.code !== 'PGRST116') {
								console.error('Error fetching service:', error)
								// Fallback to service_types if fetch fails
								if (appointment.service_types) {
									console.log('Using fallback service_types from appointment')
									setService(appointment.service_types)
								}
							} else if (data) {
								console.log('Service fetched successfully:', {
									id: data.id,
									name: data.name,
									price: data.price,
									fullData: data
								})
								setService(data)
							}
						})
					promises.push(servicePromise)
				} else if (appointment.service_types) {
					// Use the service data that's already in the appointment (no service_id case)
					console.log('Using service_types from appointment (no service_id)')
					setService(appointment.service_types)
				}

				// Wait for all promises to complete
				await Promise.all(promises)

				// Fetch grouped appointments if this appointment is part of a group
				if (appointment.group_id) {
					const { data: groupData, error: groupError } = await supabase
						.from('appointments')
						.select(`
							id,
							start_ts,
							end_ts,
							service_id,
							pet_id,
							employee_id,
							add_ons,
							comments,
							service_price,
							service_types(id, name, price, appointment_color),
							pets(id, name, species)
						`)
						.eq('group_id', appointment.group_id)
						.order('start_ts')

					if (groupError) {
						console.error('Error fetching grouped appointments:', groupError)
					} else {
						console.log('Grouped appointments fetched:', groupData)
						setGroupedAppointments(groupData || [])
					}
				} else {
					setGroupedAppointments([])
				}
			} catch (error) {
				console.error('Error fetching appointment details:', error)
			} finally {
				setLoading(false)
				setLoadedId(appointment.id ?? null)
			}
		}

		if (open && appointment) {
			fetchAppointmentData()
			setCurrentStatus(appointment.status || 'booked')
			// Ensure we start in view mode when opening
			setEditMode(false)
		} else {
			// Reset data when dialog closes
			setClient(null)
			setPet(null)
			setService(null)
			setCurrentStatus('')
			setEditMode(false)
			setGroupedAppointments([])
			setLoadedId(null)
		}
	}, [open, appointment])

	// Debug: Log when service state changes
	useEffect(() => {
		console.log('Service state updated:', {
			service: service,
			price: service?.price,
			name: service?.name
		})
	}, [service])

	const handleStatusChange = async (newStatus) => {
		if (!appointment?.id || newStatus === appointment.status) return

		setUpdatingStatus(true)
		try {
			const { error } = await supabase
				.from('appointments')
				.update({ status: newStatus })
				.eq('id', appointment.id)

			if (error) throw error

			// Update local state
			setCurrentStatus(newStatus)

			// Update the appointment object if passed by reference
			if (appointment) {
				appointment.status = newStatus
			}
		} catch (error) {
			console.error('Error updating appointment status:', error)
			// Reset to original status on error
			setCurrentStatus(appointment.status || 'booked')
		} finally {
			setUpdatingStatus(false)
		}
	}

	const handleCancelAppointment = async () => {
		if (!appointment?.id) return

		// Calculate hours until appointment
		const now = new Date()
		const appointmentStartTime = new Date(appointment.start_ts)
		const hoursUntilAppointment = (appointmentStartTime.getTime() - now.getTime()) / (1000 * 60 * 60)
		console.log('hoursUntilAppointment', hoursUntilAppointment)
		console.log('merchant?.late_cancel_hours', merchant?.late_cancel_hours)

		// Check if within late cancellation window
		const isWithinLateCancelWindow = merchant?.late_cancel_hours && hoursUntilAppointment < merchant.late_cancel_hours
		console.log('isWithinLateCancelWindow', isWithinLateCancelWindow)

		if (isWithinLateCancelWindow) {
			// Show MUI dialog with two options
			setIsWithinCancelWindow(true)
			setShowCancelChoiceDialog(true)
		} else {
			// Outside cancellation window - just cancel normally (no fee)
			setIsWithinCancelWindow(false)
			setShowCancelChoiceDialog(true)
		}
	}

	const handleCloseCancelDialog = () => {
		setShowCancelChoiceDialog(false)
	}

	const handleConfirmCancelWithFee = async () => {
		setShowCancelChoiceDialog(false)
		await handleCancelWithFee()
	}

	const handleConfirmCancelWithoutFee = async () => {
		setShowCancelChoiceDialog(false)
		await handleCancelWithoutFee()
	}

	const handleCancelWithoutFee = async () => {
		setUpdatingStatus(true)
		try {
			const cancelledAt = new Date().toISOString()

			// If this appointment is part of a group, cancel all appointments in the group
			if (appointment.group_id) {
				// Cancel all appointments in the group
				const { error: appointmentError } = await supabase
					.from('appointments')
					.update({
						status: 'cancelled',
						cancelled_at: cancelledAt
					})
					.eq('group_id', appointment.group_id)

				if (appointmentError) throw appointmentError
			} else {
				// Single appointment - cancel just this one
				const { error } = await supabase
					.from('appointments')
					.update({
						status: 'cancelled',
						cancelled_at: cancelledAt
					})
					.eq('id', appointment.id)

				if (error) throw error
				// Reminders will be deleted automatically by the sync_appointment_reminders trigger
			}

			// The slot is free again — tell the parent so the calendar drops the
			// cancelled appointment, then close instead of showing a cancelled state.
			if (appointment) {
				appointment.status = 'cancelled'
			}
			if (onAppointmentUpdated) {
				onAppointmentUpdated(appointment)
			}
			onClose()
		} catch (error) {
			console.error('Error cancelling appointment:', error)
			alert('Failed to cancel appointment: ' + error.message)
			setCurrentStatus(appointment.status || 'booked')
		} finally {
			setUpdatingStatus(false)
		}
	}

	const handleCancelWithFee = async () => {
		setUpdatingStatus(true)
		try {
			const { data, error } = await supabase.functions.invoke('cancel-appointment', {
				body: { appointmentId: appointment.id }
			})

			if (error) throw error

			if (!data.success) {
				throw new Error(data.error || 'Failed to cancel appointment')
			}

			if (appointment) {
				appointment.status = 'cancelled'
			}

			// Show fee charged information
			if (data.fee_charged && data.fee_amount > 0) {
				alert(
					`Appointment cancelled.\n\n` +
					`Fee charged: ${cs}${(data.fee_amount / 100).toFixed(2)}`
				)
			} else {
				alert('Appointment cancelled. No fee charged.')
			}

			// The slot is free again — tell the parent so the calendar drops the
			// cancelled appointment, then close instead of showing a cancelled state.
			if (onAppointmentUpdated) {
				onAppointmentUpdated(appointment)
			}
			onClose()
		} catch (error) {
			console.error('Failed to cancel appointment:', error)
			alert('Failed to cancel appointment: ' + error.message)
			setCurrentStatus(appointment.status || 'booked')
		} finally {
			setUpdatingStatus(false)
		}
	}

	// Marks the appointment no-show (charges the configured fee server-side).
	// No confirmation here — the caller confirms (window.confirm on desktop,
	// the confirm bottom sheet on mobile).
	const performNoShow = async () => {
		setUpdatingStatus(true)
		try {
			const { data, error } = await supabase.functions.invoke('mark-no-show', {
				body: { appointmentId: appointment.id }
			})

			if (error) throw error

			if (!data.success) {
				throw new Error(data.error)
			}

			// Update local state
			setCurrentStatus('noShow')
			if (appointment) {
				appointment.status = 'noShow'
			}

			// Show fee charged information
			if (data.fee_amount > 0) {
				alert(
					`Appointment marked as no-show.\n\n` +
					`Fee charged: ${cs}${(data.fee_amount / 100).toFixed(2)}`
				)
			} else {
				alert('Appointment marked as no-show. No fee charged.')
			}

		} catch (error) {
			console.error('Failed to mark as no-show:', error)
			alert('Failed to mark as no-show: ' + error.message)
			// Reset status on error
			setCurrentStatus(appointment.status || 'booked')
		} finally {
			setUpdatingStatus(false)
		}
	}

	const handleNoShow = async () => {
		const confirmed = window.confirm(
			'Mark this appointment as no-show?\n\n' +
			'This will charge the customer the configured no-show fee (if applicable).'
		)

		if (!confirmed) return
		await performNoShow()
	}

	// Undo a no-show / cancellation — reverts the status back to booked
	// (fees already charged are not refunded automatically).
	const handleUndoStatus = async () => {
		if (!appointment?.id) return
		setUpdatingStatus(true)
		try {
			const update = { status: 'booked', cancelled_at: null }
			const { error } = appointment.group_id
				? await supabase.from('appointments').update(update).eq('group_id', appointment.group_id)
				: await supabase.from('appointments').update(update).eq('id', appointment.id)
			if (error) throw error

			setCurrentStatus('booked')
			if (appointment) {
				appointment.status = 'booked'
			}
			if (onAppointmentUpdated) {
				onAppointmentUpdated(appointment)
			}
		} catch (error) {
			console.error('Failed to undo status:', error)
			alert('Failed to undo: ' + error.message)
		} finally {
			setUpdatingStatus(false)
		}
	}

	const handleCheckOut = () => {
		// Open payment dialog to log payment details
		setShowPaymentDialog(true)
	}

	const handlePaymentLogged = async () => {
		// Payment was logged successfully, update local state
		setCurrentStatus('completed')
		setShowPaymentDialog(false)

		// Re-read the row: checkout may have added add-ons and moved end_ts.
		// A new `appointment` object also re-runs the details fetch, which
		// refreshes the grouped rows (and their add-ons) for multi-pet bookings.
		let patch = { status: 'completed' }
		if (appointment?.id) {
			const { data, error } = await supabase
				.from('appointments')
				.select('status, add_ons, end_ts')
				.eq('id', appointment.id)
				.single()
			if (error) console.error('Error reloading appointment after checkout:', error)
			else if (data) patch = data
		}
		setLivePatch(patch)

		// Notify parent component (CalendarView) to refresh appointments
		// This ensures calendar reflects updated status, add-ons, and end time
		if (onAppointmentUpdated) {
			onAppointmentUpdated(appointment)
		}
	}

	const handleViewPayment = async () => {
		if (!appointment?.id) return

		setLoadingPayment(true)
		setShowPaymentDetails(true)
		try {
			const { data, error } = await supabase
				.from('payments')
				.select(PAID_RECORD_SELECT)
				.eq('appointment_id', appointment.id)
				.eq('payment_status', 'completed')
				.order('created_at', { ascending: false })
				.limit(1)
				.single()

			if (error) {
				console.error('Error fetching payment:', error)
				setPaymentDetails(null)
			} else {
				setPaymentDetails(data)
			}
		} catch (error) {
			console.error('Error fetching payment:', error)
			setPaymentDetails(null)
		} finally {
			setLoadingPayment(false)
		}
	}

	const cs = getCurrencySymbol(merchant?.currency)
	const formatCurrency = (cents) => {
		if (!cents && cents !== 0) return `${cs}0.00`
		return `${cs}${(cents / 100).toFixed(2)}`
	}

	// Silently fetch the payment record when viewing a completed appointment —
	// powers the mobile "Checked out · $X paid" banner (and pre-warms the
	// desktop payment-details dialog).
	useEffect(() => {
		if (!open || currentStatus !== 'completed' || !appointment?.id) return
		let cancelled = false
		supabase
			.from('payments')
			.select(PAID_RECORD_SELECT)
			.eq('appointment_id', appointment.id)
			.eq('payment_status', 'completed')
			.order('created_at', { ascending: false })
			.limit(1)
			.maybeSingle()
			.then(({ data, error }) => {
				if (!cancelled && !error) setPaymentDetails(data || null)
			})
		return () => { cancelled = true }
	}, [open, currentStatus, appointment?.id])

	// The list queries don't carry the Stripe columns, so look them up when a
	// cancellable appointment opens (use them straight off the object if the
	// caller already loaded them).
	useEffect(() => {
		if (!open || !appointment?.id) { setCanChargeFee(null); return }
		const hasFields = 'stripe_customer_id' in appointment && 'stripe_payment_method_id' in appointment
		if (hasFields) {
			setCanChargeFee(Boolean(appointment.stripe_customer_id && appointment.stripe_payment_method_id))
			return
		}
		let cancelled = false
		setCanChargeFee(null)
		supabase
			.from('appointments')
			.select('stripe_customer_id, stripe_payment_method_id')
			.eq('id', appointment.id)
			.maybeSingle()
			.then(({ data, error }) => {
				if (cancelled) return
				if (error) { console.error('Error checking appointment payment method:', error); setCanChargeFee(false); return }
				setCanChargeFee(Boolean(data?.stripe_customer_id && data?.stripe_payment_method_id))
			})
		return () => { cancelled = true }
	}, [open, appointment])

	// Completed appointment · invoice actions (design "Completed · more
	// actions"). View hands the paid row (plus this appointment, so the host
	// can bring the user back here from the invoice) to the host, which jumps
	// to Invoices; Download renders the same PDF the Invoices screen does.
	const viewInvoice = onOpenInvoice && paymentDetails
		? () => { onClose(); onOpenInvoice(paymentDetails, appointment) }
		: undefined
	const downloadInvoice = paymentDetails
		? async () => {
			if (downloadingInvoice) return
			setDownloadingInvoice(true)
			try {
				const { downloadInvoicePdf } = await import('./invoices/InvoicePdf')
				const inv = buildInvoiceView(paymentDetails, merchant)
				// Pet avatars + store logo for the PDF header (fallbacks without)
				const petIds = [...new Set(inv.pets.map((p) => p.petId).filter(Boolean))]
				const [petAvatars, logoPhoto] = await Promise.all([
					petIds.length > 0 ? fetchPetAvatarsBatch(petIds) : {},
					fetchMerchantLogo(merchant?.id),
				])
				await downloadInvoicePdf(inv, merchant, petAvatars, logoPhoto ? getMerchantPhotoUrl(logoPhoto) : null)
			} catch (error) {
				console.error('Error generating invoice PDF:', error)
			} finally {
				setDownloadingInvoice(false)
			}
		}
		: undefined

	// Load all clients, employees, and services for edit mode
	const loadEditModeData = async () => {
		if (!merchant?.id) return { employees: [], clients: [], services: [], addOns: [] }

		try {
			// Load all clients
			const { data: clientsData, error: clientsError } = await supabase
				.from('clients')
				.select('id, name, email, phone_number')
				.eq('merchant_id', merchant.id)
				.order('name')

			if (clientsError) throw clientsError
			setAllClients(clientsData || [])

			// Load all employees
			const { data: employeesData, error: employeesError } = await supabase
				.from('employees')
				.select('id, first_name, last_name, services')
				.eq('merchant_id', merchant.id)
				.eq('active', true)
				.order('first_name')

			if (employeesError) throw employeesError
			setAllEmployees(employeesData || [])

			// Load all services (bookable services)
			const { data: servicesData, error: servicesError } = await supabase
				.from('service_types')
				.select('id, name, duration_minutes, category, appointment_color, price')
				.eq('merchant_id', merchant.id)
				.eq('is_service', true)
				.order('name')

			if (servicesError) throw servicesError
			setAllServices(servicesData || [])

			// Load all add-ons (including duration_minutes for end time calculation)
			const { data: addOnsData, error: addOnsError } = await supabase
				.from('service_types')
				.select('id, name, price, duration_minutes')
				.eq('merchant_id', merchant.id)
				.eq('is_add_on', true)
				.order('name')

			if (addOnsError) throw addOnsError
			setAllAddOns(addOnsData || [])

			// Return the loaded data for immediate use
			return {
				employees: employeesData || [],
				clients: clientsData || [],
				services: servicesData || [],
				addOns: addOnsData || []
			}

		} catch (error) {
			console.error('Error loading edit mode data:', error)
			return { employees: [], clients: [], services: [], addOns: [] }
		}
	}

	// Load pets for selected client
	const loadClientPets = async (clientId) => {
		if (!clientId) {
			setClientPets([])
			return
		}

		setLoadingPets(true)
		try {
			const { data: petsData, error: petsError } = await supabase
				.from('pets')
				.select('*')
				.eq('client_id', clientId)
				.order('name')

			if (petsError) throw petsError
			setClientPets(petsData || [])
		} catch (error) {
			console.error('Error loading pets:', error)
			setClientPets([])
		} finally {
			setLoadingPets(false)
		}
	}

	// ── Reschedule ──────────────────────────────────────────────────────────
	// The rows of the visit (grouped) or the appointment alone, in start order.
	const rescheduleRows = () => (groupedAppointments.length > 0 ? groupedAppointments : [appointment])
	const reschedulePetNames = () => (groupedAppointments.length > 0
		? movePetList(rescheduleRows().map(r => r.pets?.name))
		: pet?.name) || appointment?.customer_name || ''

	const openReschedule = async () => {
		setRescheduleOpen(true)
		if (rescheduleEmployees.length || !merchant?.id) return
		setLoadingRescheduleEmployees(true)
		try {
			const { data, error } = await supabase
				.from('employees')
				.select('id, first_name, last_name, services')
				.eq('merchant_id', merchant.id)
				.eq('active', true)
				.order('first_name')
			if (error) throw error
			setRescheduleEmployees(data || [])
		} catch (e) {
			console.error('Error loading employees for reschedule:', e)
		} finally {
			setLoadingRescheduleEmployees(false)
		}
	}

	// Bookings' "Reschedule…" opens the panel straight into the slot picker,
	// once the visit's rows are in (so a grouped visit moves as a whole). Armed
	// per appointment open; a ref keeps the effect free of the unmemoised
	// openReschedule.
	const openRescheduleRef = useRef(null)
	openRescheduleRef.current = openReschedule
	const autoRescheduledRef = useRef(false)
	useEffect(() => { autoRescheduledRef.current = false }, [appointmentProp?.id, open])
	useEffect(() => {
		if (!open || !initialReschedule || !appointment?.id || loadedId !== appointment.id) return
		if (editMode || autoRescheduledRef.current) return
		autoRescheduledRef.current = true
		openRescheduleRef.current?.()
	}, [open, initialReschedule, appointment?.id, loadedId, editMode])

	// Tell the client about the new time on the channels their settings allow.
	// Never blocks the move: a failed send is reported in the toast instead.
	// On a non-2xx, supabase-js only says "non-2xx status code"; the function's
	// own { error, details } body lives on context (same as MessagesManager).
	const describeInvokeError = async (invokeError) => {
		let detail = ''
		try {
			const payload = invokeError?.context ? await invokeError.context.json() : null
			detail = payload?.error || payload?.details || ''
		} catch { /* body wasn't JSON — fall through to the generic message */ }
		return new Error(detail || invokeError?.message || 'Edge function call failed')
	}

	// `petNames` / `appointmentId` describe the visit as saved: the edit flow
	// may have added or removed pets in the same Save, so the loaded rows
	// (and `appointment` itself, if its pet was removed) can be stale here.
	const notifyClientOfMove = async ({ sent, date, time, previousStartTs, petNames, appointmentId }) => {
		const kinds = sent.split('+')
		const done = []
		const failed = []
		const clientName = getFullName(client) || appointment.customer_name || ''
		for (const kind of kinds) {
			try {
				if (kind === 'text') {
					const { data: smsRes, error } = await supabase.functions.invoke('send-sms', {
						body: {
							merchantId: merchant.id,
							clientId: client?.id,
							to: client?.phone_number,
							message: buildMoveMessage({ clientName, petNames, merchantName: merchant.name, date, time }),
						},
					})
					if (error) throw await describeInvokeError(error)
					if (smsRes?.error) throw new Error(smsRes.details || smsRes.error)
					done.push('text')
				} else if (kind === 'email') {
					// The client's current address wins over the email copied onto the
					// row at booking time (it may be stale or missing).
					const { data: emailRes, error } = await supabase.functions.invoke('send-appointment-email', {
						body: {
							type: 'rescheduled',
							appointmentId: appointmentId || appointment.id,
							previousStartTs,
							customerEmail: client?.email || undefined,
						},
					})
					if (error) throw await describeInvokeError(error)
					if (emailRes?.error) throw new Error(emailRes.error)
					done.push('email')
				}
			} catch (e) {
				console.error(`Failed to ${kind} the client about the move:`, e)
				failed.push(kind)
			}
		}
		return { notified: done.join('+') || null, failed }
	}

	const performReschedule = async ({ date, time, groomer, sent }) => {
		if (!appointment?.id || !merchant) return
		const tz = merchant.timezone || 'UTC'
		const rows = rescheduleRows()
		const previousStartTs = rows[0].start_ts
		const newStart = dayjs.tz(`${date} ${time}`, tz)
		if (!newStart.isValid()) return
		const deltaMs = newStart.valueOf() - dayjs(previousStartTs).valueOf()
		const reassign = groomer && groomer !== 'booked' ? groomer : null
		const employeePatch = reassign ? { employee_id: reassign } : {}

		setRescheduling(true)
		try {
			// Shift every row of the visit by the same delta so pets keep their
			// order and durations; only start/end (and optionally the groomer)
			// change — the rows themselves stay, so invoices, reminders and
			// the group id survive (reminders re-sync via the DB trigger).
			const updates = rows.map(r => ({
				id: r.id,
				start_ts: dayjs(r.start_ts).add(deltaMs, 'millisecond').toISOString(),
				end_ts: dayjs(r.end_ts).add(deltaMs, 'millisecond').toISOString(),
			}))
			for (const u of updates) {
				const { error } = await supabase
					.from('appointments')
					.update({ start_ts: u.start_ts, end_ts: u.end_ts, ...employeePatch })
					.eq('id', u.id)
				if (error) throw error
			}

			// Mirror the change locally so the details view updates at once.
			const emp = reassign ? rescheduleEmployees.find(e => e.id === reassign) : null
			const empPatch = emp
				? { employee_id: emp.id, employees: { id: emp.id, first_name: emp.first_name, last_name: emp.last_name } }
				: {}
			const main = updates.find(u => u.id === appointment.id) || updates[0]
			setLivePatch(prev => ({ ...(prev || {}), start_ts: main.start_ts, end_ts: main.end_ts, ...empPatch }))
			setGroupedAppointments(prev => prev.map(r => {
				const u = updates.find(x => x.id === r.id)
				return u ? { ...r, start_ts: u.start_ts, end_ts: u.end_ts, ...(emp ? { employee_id: emp.id } : {}) } : r
			}))

			let notified = null
			let failed = []
			if (sent) {
				const res = await notifyClientOfMove({ sent, date, time, previousStartTs, petNames: reschedulePetNames() })
				notified = res.notified
				failed = res.failed
			}
			setMoved({ fromStartTs: previousStartTs, notified })
			setRescheduleOpen(false)

			const first = moveFirstName(getFullName(client) || appointment.customer_name || 'the client')
			const when = `${newStart.format('ddd, MMM D')} · ${newStart.format('h:mm A')}`
			const verb = notifiedVerb(notified)
			ping(`Moved to ${when}`
				+ (verb ? ` · ${first} ${verb}` : '')
				+ (failed.length ? ` · couldn’t ${failed.join(' or ')} ${first}` : ''))

			if (onAppointmentUpdated) {
				onAppointmentUpdated({ ...appointment, start_ts: main.start_ts, end_ts: main.end_ts, ...empPatch })
			}
		} catch (e) {
			console.error('Failed to reschedule appointment:', e)
			alert('Failed to reschedule: ' + (e.message || 'unknown error'))
		} finally {
			setRescheduling(false)
		}
	}

	// ── Edit flow: When field → slot picker, Save → move confirm ──────────────
	const editEmpIds = () => moveUniq(editModeAppointments.map(a => a.employee?.id))
	const editMins = () => editModeAppointments.reduce((sum, a) => sum + getAppointmentDuration(a), 0)
	const editPetNames = () => movePetList(editModeAppointments.map(a => a.pet?.name))
		|| pet?.name || appointment?.customer_name || ''

	// A pick updates the draft only; nothing is written until Save. A groomer
	// chosen in the picker applies to every pet (one groomer per visit).
	const handleEditSlotPick = (p) => {
		setEditModeDate(p.date)
		setEditModeTime(p.time)
		setEditPick(p)
		if (p.groomer && p.groomer !== 'booked') {
			const emp = allEmployees.find(e => e.id === p.groomer)
			if (emp) setEditModeAppointments(prev => prev.map(a => ({ ...a, employee: emp })))
		}
		setEditSlotOpen(false)
	}

	// Save with a moved start time → confirm step first (returns true to hold
	// the save). The pick's verdict is reused when it still matches the draft.
	const interceptEditSave = ({ date, time }) => {
		const o = editOriginalRef.current
		if (!o || (date === o.date && time === o.time)) return false
		const pick = editPick && editPick.date === date && editPick.time === time ? editPick : null
		setEditMoveConfirm({
			date, time,
			groomer: pick?.groomer || 'booked',
			empIds: pick?.empIds || editEmpIds(),
			check: pick?.check,
		})
		return true
	}

	// Confirmed: save through the overview (rows updated in place, same as any
	// edit) and notify afterwards from handleEditSaveSuccess. If the save
	// fails the overview shows its error, so drop back to the form.
	const confirmEditMove = async (sent) => {
		const c = editMoveConfirm
		if (!c || !overviewSaveRef.current) return
		editMoveRef.current = {
			sent, date: c.date, time: c.time, previousStartTs: editOriginalRef.current?.startTs,
			petNames: editPetNames(),
			appointmentId: editModeAppointments.find(a => a.id)?.id || appointment?.id,
		}
		setEditSaving(true)
		try {
			await overviewSaveRef.current({ skipMoveConfirm: true })
		} finally {
			setEditSaving(false)
			if (editMoveRef.current) {
				editMoveRef.current = null
				setEditMoveConfirm(null)
			}
		}
	}

	const renderEditSlotPicker = (mobile) => {
		const o = editOriginalRef.current
		const mins = editMins()
		const empIds = editEmpIds()
		const sub = `${mins} mins · ${moveGroomerNames(empIds, allEmployees, true)}`
		return (
			<RescheduleOverlay mobile={mobile} title="Change date & time" sub={sub} onClose={() => setEditSlotOpen(false)}>
				<RescheduleSlotPicker
					merchant={merchant}
					employees={allEmployees}
					bookedIds={empIds}
					serviceIds={moveUniq(editModeAppointments.map(a => a.service?.id))}
					excludeIds={[...editModeAppointments.map(a => a.id).filter(Boolean), ...editModeRemovedIds]}
					mins={mins}
					current={o ? { date: o.date, time: o.time } : null}
					value={editModeDate && editModeTime ? { date: editModeDate, time: editModeTime } : null}
					onPick={handleEditSlotPick}
				/>
			</RescheduleOverlay>
		)
	}

	const renderEditMoveConfirm = (mobile) => {
		const o = editOriginalRef.current
		if (!o || !editMoveConfirm) return null
		return (
			<RescheduleConfirm
				mobile={mobile}
				from={{ date: o.date, time: o.time }}
				fromEmpIds={o.empIds}
				to={editMoveConfirm}
				mins={editMins()}
				petNames={editPetNames()}
				clientName={getFullName(client) || appointment?.customer_name || ''}
				client={client}
				merchant={merchant}
				employees={allEmployees}
				notify={editNotify}
				setNotify={setEditNotify}
				saving={editSaving}
				onBack={() => { if (!editSaving) setEditMoveConfirm(null) }}
				onConfirm={confirmEditMove}
				onOpenClient={client ? () => setEditClientOpen(true) : null}
			/>
		)
	}

	// The slot picker + confirm step, layered over the details view.
	const renderRescheduleFlow = (mobile) => {
		const tz = merchant?.timezone || 'UTC'
		const rows = rescheduleRows()
		const first = dayjs(rows[0].start_ts).tz(tz)
		const last = dayjs(rows[rows.length - 1].end_ts).tz(tz)
		const mins = Math.max(0, last.diff(first, 'minute'))
		const petNames = reschedulePetNames()
		return (
			<RescheduleFlow
				mobile={mobile}
				merchant={merchant}
				employees={rescheduleEmployees}
				loadingEmployees={loadingRescheduleEmployees}
				bookedIds={moveUniq(rows.map(r => r.employee_id))}
				serviceIds={moveUniq(rows.map(r => r.service_id))}
				excludeIds={rows.map(r => r.id)}
				mins={mins}
				current={{ date: first.format('YYYY-MM-DD'), time: first.format('HH:mm') }}
				petNames={petNames}
				clientName={getFullName(client) || appointment.customer_name || ''}
				client={client}
				saving={rescheduling}
				onClose={() => { if (!rescheduling) setRescheduleOpen(false) }}
				onConfirm={performReschedule}
				onOpenClient={client ? () => setEditClientOpen(true) : null}
			/>
		)
	}

	// design .apd-toast
	const renderToast = () => (toast ? (
		<Box sx={{
			position: 'absolute', bottom: 88, left: '50%', transform: 'translateX(-50%)', zIndex: 45,
			maxWidth: 'calc(100% - 32px)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
			bgcolor: '#2E2A40', color: '#FFFFFF', fontSize: '12.5px', fontWeight: 650, fontFamily: L.font,
			p: '9px 14px', borderRadius: '10px', boxShadow: '0 8px 22px rgba(46, 42, 64, 0.32)',
		}}>
			{toast}
		</Box>
	) : null)

	// Handle entering edit mode
	const handleEnterEditMode = async () => {
		// Guard against double-clicks on Edit while the data below is loading.
		if (enteringEditRef.current) return
		enteringEditRef.current = true
		setEnteringEdit(true)
		try {
			await enterEditMode()
		} finally {
			enteringEditRef.current = false
			setEnteringEdit(false)
		}
	}

	const enterEditMode = async () => {
		// editMode is NOT set here: flipping it before the awaits below leaves a
		// window where editMode is true but editModeAppointments is still empty,
		// and the desktop drawer renders the legacy edit chrome in that state —
		// a flash of the old design on a cold connection. The flag flips at the
		// end, in the same batch as the prepared edit-mode state, so the view
		// jumps straight from details to the edit overview.
		const { employees: loadedEmployees, addOns: loadedAddOns, services: loadedServices } = await loadEditModeData()

		// Set current selections for legacy edit mode (keeping for backwards compatibility)
		setSelectedClient(client)
		setSelectedService(service) // Set current service

		// Initialize date and time from appointment for rescheduling
		// For grouped appointments, use the earliest start time (first appointment in the group)
		const startTs = groupedAppointments.length > 1
			? groupedAppointments[0].start_ts
			: appointment.start_ts
		const appointmentStart = dayjs(startTs).tz(merchant?.timezone || 'UTC')
		setSelectedDate(appointmentStart.format('YYYY-MM-DD'))
		setSelectedTime(appointmentStart.format('HH:mm'))

		// Set edit mode date/time for AppointmentsOverview
		setEditModeDate(appointmentStart.format('YYYY-MM-DD'))
		setEditModeTime(appointmentStart.format('HH:mm'))

		// The visit's current slot — what the When field compares against and
		// what the move-confirm step shows as "from".
		editOriginalRef.current = {
			date: appointmentStart.format('YYYY-MM-DD'),
			time: appointmentStart.format('HH:mm'),
			startTs: startTs,
			empIds: moveUniq((groupedAppointments.length > 1 ? groupedAppointments : [appointment]).map(r => r.employee_id)),
		}
		editMoveRef.current = null
		setEditSlotOpen(false)
		setEditPick(null)
		setEditMoveConfirm(null)
		setEditNotify(true)

		// Transform appointments for AppointmentsOverview
		const appointmentsToEdit = []

		if (groupedAppointments.length > 1) {
			// Grouped appointments - transform all
			for (const appt of groupedAppointments) {
				// Find full service object
				const fullService = loadedServices.find(s => s.id === appt.service_id) || appt.service_types

				// Find full employee object
				const fullEmployee = appt.employee_id
					? loadedEmployees.find(emp => emp.id === appt.employee_id)
					: null

				// Transform add-ons
				const transformedAddOns = (appt.add_ons || []).map(addon => {
					const fullAddon = loadedAddOns.find(a => a.id === addon.service_id)
					return fullAddon || {
						id: addon.service_id,
						name: addon.name,
						price: addon.price,
						duration_minutes: addon.duration_minutes || 0
					}
				})

				appointmentsToEdit.push({
					id: appt.id,
					pet: appt.pets,
					service: fullService,
					addOns: transformedAddOns,
					employee: fullEmployee,
					comments: appt.comments || appointment?.comments,
					// Include timestamps for duration calculation
					start_ts: appt.start_ts,
					end_ts: appt.end_ts
				})
			}
		} else {
			// Single appointment
			// Find full employee object
			const fullEmployee = appointment?.employee_id
				? loadedEmployees.find(emp => emp.id === appointment.employee_id)
				: (appointment?.employees || null)

			// Transform add-ons
			const transformedAddOns = (appointment?.add_ons || []).map(addon => {
				const fullAddon = loadedAddOns.find(a => a.id === addon.service_id)
				return fullAddon || {
					id: addon.service_id,
					name: addon.name,
					price: addon.price,
					duration_minutes: addon.duration_minutes || 0
				}
			})

			appointmentsToEdit.push({
				id: appointment.id,
				pet: pet,
				service: service,
				addOns: transformedAddOns,
				employee: fullEmployee,
				comments: appointment?.comments,
				// Include timestamps for duration calculation
				start_ts: appointment.start_ts,
				end_ts: appointment.end_ts
			})
		}

		setEditModeAppointments(appointmentsToEdit)
		setEditModeRemovedIds([])

		// Initialize selected add-ons from appointment data (for legacy edit mode)
		if (appointment?.add_ons && Array.isArray(appointment.add_ons) && appointment.add_ons.length > 0) {
			// Map appointment add-ons to full add-on objects from the loaded list
			const preSelectedAddOns = appointment.add_ons
				.map(apptAddon => {
					// Find matching add-on in available add-ons list
					const fullAddon = loadedAddOns.find(a => a.id === apptAddon.service_id)
					if (fullAddon) {
						return fullAddon
					}
					// If add-on not found in list (maybe deleted), use stored data
					return {
						id: apptAddon.service_id,
						name: apptAddon.name,
						price: apptAddon.price
					}
				})
				.filter(Boolean)
			setSelectedAddOns(preSelectedAddOns)
		} else {
			setSelectedAddOns([])
		}

		// Find current employee from appointment using the freshly loaded data
		if (appointment?.employee_id && loadedEmployees.length > 0) {
			const currentEmployee = loadedEmployees.find(emp => emp.id === appointment.employee_id)
			setSelectedEmployee(currentEmployee || null)
		} else if (appointment?.employees) {
			setSelectedEmployee(appointment.employees)
		}

		// Load pets for current client
		if (appointment?.user_id) {
			await loadClientPets(appointment.user_id)
		}

		// Everything the edit UI needs is in place — enter edit mode in the same
		// render as the prepared state (see note at the top of this function).
		setEditMode(true)
	}

	// Handle canceling edit mode
	const handleCancelEdit = () => {
		setEditMode(false)
		setSelectedClient(null)
		setSelectedEmployee(null)
		setSelectedService(null)
		setSelectedAddOns([])
		setSelectedDate('')
		setSelectedTime('')
		setClientPets([])
		setEditModeAppointments([])
		setEditModeRemovedIds([])
		setEditModeDate('')
		setEditModeTime('')
		setEditModeStep('overview')
		setEditingAppointmentIndex(null)
		editOriginalRef.current = null
		editMoveRef.current = null
		setEditSlotOpen(false)
		setEditPick(null)
		setEditMoveConfirm(null)
	}

	// Watch for client changes in edit mode
	useEffect(() => {
		if (editMode && selectedClient?.id) {
			loadClientPets(selectedClient.id)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedClient?.id, editMode])

	// Watch for service changes in edit mode - validate employee capability
	useEffect(() => {
		if (editMode && selectedService && selectedEmployee) {
			// Check if selected employee can perform the selected service
			const canPerform = canEmployeePerformService(selectedEmployee, selectedService.id)
			if (!canPerform) {
				// Clear employee selection if they can't perform the service
				setSelectedEmployee(null)
			}
		}
		// Note: End time will be updated automatically when saving based on new service duration
	}, [editMode, selectedService, selectedEmployee])

	if (!appointment) {
		return null
	}

	const start = dayjs(appointment.start_ts).tz(merchant?.timezone || 'UTC')
	const end = dayjs(appointment.end_ts).tz(merchant?.timezone || 'UTC')

	// Calculate end time based on selected service duration + add-ons in edit mode
	const getDisplayEndTime = () => {
		if (editMode && selectedService && selectedService.duration_minutes && selectedDate && selectedTime) {
			// Calculate total duration: service duration + all selected add-ons durations
			const addOnsDuration = selectedAddOns.reduce((sum, addon) => sum + (addon.duration_minutes || 0), 0)
			const totalDuration = selectedService.duration_minutes + addOnsDuration
			// Use selected date/time as the start for calculating end time
			const editStartTime = dayjs.tz(`${selectedDate} ${selectedTime}`, merchant?.timezone || 'UTC')
			return editStartTime.add(totalDuration, 'minute')
		}
		// Use original end time when not in edit mode or no service selected
		return end
	}

	// Get start time for display in edit mode
	const getDisplayStartTime = () => {
		if (editMode && selectedDate && selectedTime) {
			return dayjs.tz(`${selectedDate} ${selectedTime}`, merchant?.timezone || 'UTC')
		}
		return start
	}

	const displayEndTime = getDisplayEndTime()

	const formatTime = (date) => {
		return date.format('h:mm A')
	}

	// Handle edit appointment from AppointmentsOverview
	const handleEditAppointmentFromOverview = (index) => {
		setEditingAppointmentIndex(index)
		setEditModeStep('single')
	}

	// Handle add pet in edit mode
	const handleAddPetInEditMode = () => {
		setEditingAppointmentIndex(null)
		setEditModeStep('single')
	}

	// Handle save from AddSingleAppointment in edit mode
	const handleSingleSaveInEditMode = (appointmentData) => {
		if (editingAppointmentIndex !== null) {
			// Editing existing appointment
			setEditModeAppointments(prev => {
				// The visit shares one employee: changing it on the first pet
				// re-assigns every pet in the group.
				const updated = prev.map((apt, i) => (
					editingAppointmentIndex === 0 && i !== 0
						? { ...apt, employee: appointmentData.employee }
						: apt
				))
				updated[editingAppointmentIndex] = {
					...updated[editingAppointmentIndex],
					pet: appointmentData.pet,
					service: appointmentData.service,
					addOns: appointmentData.addOns,
					employee: appointmentData.employee
				}
				return updated
			})
		} else {
			// Adding new appointment
			setEditModeAppointments(prev => [...prev, {
				pet: appointmentData.pet,
				service: appointmentData.service,
				addOns: appointmentData.addOns,
				employee: appointmentData.employee
			}])
		}
		setEditingAppointmentIndex(null)
		setEditModeStep('overview')
	}

	// Handle cancel from AddSingleAppointment in edit mode
	const handleSingleCancelInEditMode = () => {
		setEditingAppointmentIndex(null)
		setEditModeStep('overview')
	}

	// Handle delete appointment from AppointmentsOverview.
	// Removing a pet only updates local state: existing rows are remembered in
	// editModeRemovedIds and deleted when the merchant saves. Removing the last
	// pet keeps the editor open — at least one pet must be added before Save.
	const handleDeleteAppointmentFromOverview = (index) => {
		const aptToDelete = editModeAppointments[index]
		if (aptToDelete?.id) {
			setEditModeRemovedIds(prev => prev.includes(aptToDelete.id) ? prev : [...prev, aptToDelete.id])
		}
		setEditModeAppointments(prev => prev.filter((_, i) => i !== index))
	}

	// Handle successful save from AppointmentsOverview. Async so a confirmed
	// move can tell the client before the panel closes (the overview awaits
	// it and keeps its Saving… state meanwhile).
	const handleEditSaveSuccess = async () => {
		const move = editMoveRef.current
		editMoveRef.current = null
		if (move?.sent) {
			const { failed } = await notifyClientOfMove({
				sent: move.sent, date: move.date, time: move.time, previousStartTs: move.previousStartTs,
				petNames: move.petNames, appointmentId: move.appointmentId,
			})
			if (failed.length) {
				const first = moveFirstName(getFullName(client) || appointment?.customer_name || 'the client')
				alert(`Changes saved, but we couldn’t ${failed.join(' or ')} ${first} about the new time.`)
			}
		}

		setEditMode(false)
		setEditModeAppointments([])
		setEditModeRemovedIds([])
		setEditModeDate('')
		setEditModeTime('')
		setEditModeStep('overview')
		setEditingAppointmentIndex(null)
		editOriginalRef.current = null
		setEditSlotOpen(false)
		setEditPick(null)
		setEditMoveConfirm(null)

		// Notify parent component that appointment was updated
		if (onAppointmentUpdated) {
			onAppointmentUpdated(appointment)
		}

		// Close the dialog
		onClose()
	}

	// Get pet IDs that are already added in edit mode (for filtering in AddSingleAppointment)
	const editModeExistingPetIds = editModeAppointments
		.filter((_, i) => i !== editingAppointmentIndex) // Exclude currently editing
		.map(apt => apt.pet?.id)
		.filter(Boolean)

	// Get the appointment being edited in single step
	const editingAppointmentForSingle = editingAppointmentIndex !== null
		? editModeAppointments[editingAppointmentIndex]
		: null

	// Render Mobile Content with Figma Design
	const renderMobileContent = () => {
		// Edit mode - show either AddSingleAppointment or AppointmentsOverview
		if (editMode) {
			// Single appointment editing step
			if (editModeStep === 'single') {
				// Get all appointment IDs being edited (to exclude from overlap check)
				const allEditingAppointmentIds = [
					...editModeAppointments.map(apt => apt.id).filter(Boolean),
					...editModeRemovedIds
				]

				// Calculate total duration of appointments that come BEFORE the one being edited
				// For new appointments (editingAppointmentIndex === null), include all existing appointments
				// For editing appointment at index N, only include appointments 0 to N-1
				const existingAppointmentsDuration = editModeAppointments
					.filter((_, i) => editingAppointmentIndex === null ? true : i < editingAppointmentIndex)
					.reduce((sum, apt) => {
						const serviceDuration = apt.service?.duration_minutes || 0
						const addOnsDuration = (apt.addOns || []).reduce((s, a) => s + (a.duration_minutes || 0), 0)
						return sum + serviceDuration + addOnsDuration
					}, 0)

				return (
					<AddSingleAppointment
						merchant={merchant}
						isFirstPet={false} // Never first pet in edit mode - client/date/time already set
						selectedClient={client}
						selectedDate={editModeDate}
						selectedTime={editModeTime}
						existingPetIds={editModeExistingPetIds}
						existingAppointmentsDuration={existingAppointmentsDuration}
						editingAppointment={editingAppointmentForSingle}
						excludeAppointmentIds={allEditingAppointmentIds}
						// One employee per visit (same rule as the add flow): pets after
						// the first are locked to the first pet's employee. Editing the
						// first pet keeps the field open — that's where it's changed.
						lockedEmployee={editingAppointmentIndex !== 0 ? editModeAppointments[0]?.employee ?? null : null}
						onSave={handleSingleSaveInEditMode}
						onCancel={handleSingleCancelInEditMode}
						isMobile={isMobile}
						saveActionRef={singleAppointmentSaveRef}
					/>
				)
			}

			// Overview step
			return (
				<AppointmentsOverview
					merchant={merchant}
					client={client}
					date={editModeDate}
					time={editModeTime}
					appointments={editModeAppointments}
					removedAppointmentIds={editModeRemovedIds}
					onTimeChange={setEditModeTime}
					onDateChange={setEditModeDate}
					onAddPet={handleAddPetInEditMode}
					onEditAppointment={handleEditAppointmentFromOverview}
					onDeleteAppointment={handleDeleteAppointmentFromOverview}
					onBook={handleEditSaveSuccess}
					onCancel={handleCancelEdit}
					isMobile={isMobile}
					isEditMode={true}
					groupId={appointment?.group_id}
					onAppointmentsChange={setEditModeAppointments}
					saveActionRef={overviewSaveRef}
					onPickSlot={() => setEditSlotOpen(true)}
					onInterceptSave={interceptEditSave}
				/>
			)
		}

		// View mode uses the details-based design from Figma 667-21907
		// Calculate total price including add-ons (same pattern as LogPaymentDialog.js)
		// For grouped appointments: sum service prices + deduplicated add-ons (add-ons are shared across group)
		const servicePrice = groupedAppointments.length > 0
			? groupedAppointments.reduce((sum, appt) => sum + getServicePrice(appt), 0)
			: getServicePrice(appointment, service)

		// Calculate add-ons price from ALL appointments (no deduplication).
		// Each row holds only its own add-ons (checkout distributes them without
		// duplication — see checkoutUtils.distributeAddOns), so summing the rows
		// matches the payment total.
		const addOnsPrice = storedAddOnsTotal(
			groupedAppointments.length > 0 ? groupedAppointments : [appointment].filter(Boolean)
		)

		const totalServicePrice = servicePrice + addOnsPrice

		// Get time range for grouped appointments
		const groupStartTime = groupedAppointments.length > 0
			? dayjs(groupedAppointments[0].start_ts).tz(merchant?.timezone || 'UTC')
			: start
		const groupEndTime = groupedAppointments.length > 0
			? dayjs(groupedAppointments[groupedAppointments.length - 1].end_ts).tz(merchant?.timezone || 'UTC')
			: end

		return (
			<>
				{/* Container for Service, Status, Employee, and Date */}
				<Box sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: '4px'
				}}>
					{/* Service and Status Row */}
					<Box sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						gap: '12px'
					}}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
							<Box sx={{
								width: '16px',
								height: '16px',
								bgcolor: COLOR_OPTIONS.find(opt => opt.value === service?.appointment_color)?.color || '#61B883',
								borderRadius: '4px'
							}} />
							<Typography sx={{
								fontWeight: 600,
								fontSize: '24px',
								lineHeight: 1.5,
								color: '#2D3748'
							}}>
								{groupedAppointments.length > 1
									? `${groupedAppointments.length} Services`
									: (service?.name || 'Service')}
							</Typography>
						</Box>

						{/* Status Badge */}
						<Box sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							gap: '6px',
							minWidth: '95px',
							bgcolor: getAppointmentStatusDisplay(appointment.status).bgcolor,
							borderRadius: '9999px',
							p: '0 12px',
							height: '32px'
						}}>
							<Typography sx={{
								fontSize: '13px',
								lineHeight: 1.5,
								color: getAppointmentStatusDisplay(appointment.status).color
							}}>
								{STATUS_OPTIONS.find(s => s.value === currentStatus)?.label || currentStatus}
							</Typography>
						</Box>
					</Box>

					{/* Add-ons display for single appointments */}
					{groupedAppointments.length <= 1 && appointment?.add_ons && Array.isArray(appointment.add_ons) && appointment.add_ons.length > 0 && (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', mt: '4px' }}>
							{appointment.add_ons.map((addon, index) => (
								<Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
									<Typography sx={{ fontSize: '14px', fontWeight: 400, color: '#718096' }}>
										+ {addon.name}
									</Typography>
									<Typography sx={{ fontSize: '14px', fontWeight: 400, color: '#718096' }}>
								{cs}{((addon.price || 0) / 100).toFixed(2)}
								</Typography>
							</Box>
						))}
					</Box>
				)}

				{/* Employee Info */}
					<Typography sx={{
						fontSize: '16px',
						fontWeight: 400,
						lineHeight: 1.5,
						color: '#2D3748'
					}}>
						Serviced by: {appointment.employees?.first_name && appointment.employees?.last_name
							? `${appointment.employees.first_name} ${appointment.employees.last_name}`
							: 'Not assigned'
						}
					</Typography>

					{/* Date and Time */}
					<Typography sx={{
						fontWeight: 600,
						fontSize: '16px',
						color: '#2D3748'
					}}>
						{groupStartTime.format('dddd, MMMM D')}
					</Typography>
					<Typography sx={{
						fontWeight: 600,
						fontSize: '16px',
						color: '#2D3748'
					}}>
						{formatTime(groupStartTime)} - {formatTime(groupEndTime)}
					</Typography>
				</Box>

				{/* Grouped Appointments List */}
				{groupedAppointments.length > 1 && (
					<Box sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: '8px',
						bgcolor: isMobile ? '#FFFFFF' : '#F7F6F3',
						borderRadius: '8px',
						p: '12px'
					}}>
						<Typography sx={{
							fontWeight: 600,
							fontSize: '14px',
							color: '#2D3748',
							mb: '4px'
						}}>
							Services in this visit:
						</Typography>
						{groupedAppointments.map((appt, index) => (
							<Box key={appt.id} sx={{
								display: 'flex',
								flexDirection: 'column',
								py: '8px',
								borderBottom: index < groupedAppointments.length - 1 ? '1px solid #E2E8F0' : 'none'
							}}>
								<Box sx={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center'
								}}>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
										<Box sx={{
											width: '12px',
											height: '12px',
											bgcolor: COLOR_OPTIONS.find(opt => opt.value === appt.service_types?.appointment_color)?.color || '#61B883',
											borderRadius: '3px'
										}} />
										<Box>
											<Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#2D3748' }}>
												{appt.service_types?.name || 'Service'}
											</Typography>
											<Typography sx={{ fontSize: '12px', color: '#A0AEC0' }}>
												{appt.pets?.name || 'Pet'} • {dayjs(appt.start_ts).tz(merchant?.timezone || 'UTC').format('h:mm A')} - {dayjs(appt.end_ts).tz(merchant?.timezone || 'UTC').format('h:mm A')}
											</Typography>
										</Box>
									</Box>
									<Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#2D3748' }}>
										{cs}{(getServicePrice(appt) / 100).toFixed(2)}
									</Typography>
								</Box>
								{/* Add-ons for this service */}
								{appt.add_ons && Array.isArray(appt.add_ons) && appt.add_ons.length > 0 && (
									<Box sx={{ pl: '24px', mt: '4px' }}>
										{appt.add_ons.map((addon, addonIndex) => (
											<Box key={addonIndex} sx={{
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'center',
												py: '2px'
											}}>
												<Typography sx={{ fontSize: '12px', color: '#718096' }}>
													+ {addon.name}
												</Typography>
												<Typography sx={{ fontSize: '12px', color: '#718096' }}>
											{cs}{((addon.price || 0) / 100).toFixed(2)}
											</Typography>
										</Box>
									))}
								</Box>
							)}
						</Box>
					))}
				</Box>
				)}

				{/* Pet Card - Only show for single appointments */}
				{pet && groupedAppointments.length <= 1 && (
					<Box sx={{
						display: 'flex',
						alignItems: 'center',
						gap: '16px',
						bgcolor: isMobile ? '#FFFFFF' : '#F7F6F3', // White on mobile, grey on desktop
						borderRadius: '8px',
						p: '12px'
					}}>
						{/* Pet Image Placeholder */}
						<Box sx={{
							width: '56px',
							height: '56px',
							borderRadius: '9999px',
							bgcolor: '#E2E8F0',
							border: '2px solid #F9FAFB',
							boxShadow: '0px 0px 0px 3px rgba(203, 213, 224, 1)'
						}} />

						<Box sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: 0
						}}>
							<Typography sx={{
								fontWeight: 600,
								fontSize: '24px',
								lineHeight: 1.5,
								color: '#2D3748'
							}}>
								{pet.name}
							</Typography>
							<Box sx={{ display: 'flex', gap: '12px' }}>
								<Typography sx={{
									fontSize: '13px',
									lineHeight: 1.5,
									color: '#A0AEC0'
								}}>
									Breed: {pet.species || 'Unknown'}
								</Typography>
								{pet.weight && (
									<Typography sx={{
										fontSize: '13px',
										lineHeight: 1.5,
										color: '#A0AEC0'
									}}>
										Size: {pet.weight} lbs
									</Typography>
								)}
							</Box>
						</Box>
					</Box>
				)}

				{/* Client Contact Card */}
				{client && (
					<Box sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: '12px',
						bgcolor: isMobile ? '#FFFFFF' : '#F7F6F3', // White on mobile, grey on desktop
						borderRadius: '8px',
						p: '12px'
					}}>
						<Typography sx={{
							fontWeight: 600,
							fontSize: '16px',
							lineHeight: 1.21,
							color: '#2D3748'
						}}>
							{getFullName(client)}
						</Typography>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
							{client.email && (
								<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
									<EmailIcon sx={{ fontSize: 16, color: '#000000' }} />
									<Typography sx={{
										fontSize: '13px',
										lineHeight: 1.5,
										color: '#A0AEC0'
									}}>
										{client.email}
									</Typography>
								</Box>
							)}
							{client.phone_number && (
								<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
									<PhoneIcon sx={{ fontSize: 16, color: '#000000' }} />
									<Typography sx={{
										fontSize: '13px',
										lineHeight: 1.5,
										color: '#A0AEC0'
									}}>
										{client.phone_number}
									</Typography>
								</Box>
							)}
						</Box>
					</Box>
				)}

				{/* Booking Notes */}
				{appointment.comments && (
					<Box sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: '4px'
					}}>
						<Typography sx={{
							fontSize: '12px',
							lineHeight: 1.21,
							letterSpacing: '-2.08%',
							color: '#2D3748'
						}}>
							Booking notes
						</Typography>
						<Box sx={{
							p: '12px 16px',
							bgcolor: '#FFFFFF', // Always white for input fields
							border: '1px solid #CBD5E0',
							borderRadius: '6px'
						}}>
							<Typography sx={{
								fontSize: '14px',
								lineHeight: 1.21,
								letterSpacing: '-1.79%',
								color: '#757575'
							}}>
								{appointment.comments}
							</Typography>
						</Box>
					</Box>
				)}

				{/* Subtotal and Actions */}
				<Box sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: '10px',
					flex: 1,
					justifyContent: 'flex-end'
				}}>
					<Box sx={{
						width: '100%',
						height: '1px',
						bgcolor: '#CBD5E0'
					}} />

					<Box sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center'
					}}>
						<Typography sx={{
							fontWeight: 600,
							fontSize: '16px',
							lineHeight: 2.25,
							color: '#2D3748'
						}}>
							Subtotal
						</Typography>
						<Typography sx={{
							fontWeight: 600,
							fontSize: '16px',
							lineHeight: 2.25,
							color: '#2D3748'
						}}>
							{cs}{(totalServicePrice / 100).toFixed(2)}
						</Typography>
					</Box>

					<Box sx={{ display: 'flex', gap: '10px' }}>
						<Button
							onClick={handleCheckOut}
							variant="contained"
							disabled={updatingStatus || currentStatus === 'completed'}
							fullWidth
							sx={{
								p: '12px 16px',
								bgcolor: '#E9D8FD',
								border: '1px solid #9F7AEA',
								borderRadius: '6px',
								color: '#2D3748',
								fontWeight: 600,
								fontSize: '14px',
								letterSpacing: '-1.79%',
								textTransform: 'none',
								'&:hover': {
									bgcolor: '#E9D8FD'
								}
							}}
						>
							Check out
						</Button>
						<IconButton
							onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
							sx={{
								border: '1px solid #CBD5E0',
								borderRadius: '6px',
								bgcolor: '#FFFFFF',
								width: '48px',
								height: '48px'
							}}
						>
							<MoreVertIcon sx={{ fontSize: 24, color: '#9F7AEA' }} />
						</IconButton>
					</Box>
				</Box>
			</>
		)
	}

	// Mobile edit-mode header — same chrome as the mobile add-appointment flow:
	// 23px title + round X (exits edit mode) + step subtitle.
	const renderMobileEditHeader = () => {
		const isSingle = editModeStep === 'single'
		const subtitle = isSingle
			? (editingAppointmentIndex !== null
				? `Editing ${editModeAppointments[editingAppointmentIndex]?.pet?.name || 'pet'}`
				: (editModeAppointments.length === 0 ? 'Add a pet' : 'Add another pet'))
			: 'Review & save changes'
		return (
			<Box sx={{ flex: 'none', p: 'calc(env(safe-area-inset-top, 0px) + 20px) 16px 10px', fontFamily: L.font }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
					<Typography component="h1" sx={{
						flex: 1, minWidth: 0, m: 0, fontSize: '23px', fontWeight: 800,
						letterSpacing: '-0.02em', color: L.text, fontFamily: L.font,
						whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
					}}>
						Edit appointment
					</Typography>
					<Box
						component="button"
						type="button"
						aria-label="Close"
						onClick={handleCancelEdit}
						sx={{
							width: 36, height: 36, flex: 'none', borderRadius: '50%',
							border: `1.5px solid ${L.border}`, bgcolor: '#FFFFFF', color: L.muted,
							display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
							'&:active': { bgcolor: L.surface, color: L.text },
						}}
					>
						<Icon name="x" size={15} sw={2} />
					</Box>
				</Box>
				<Typography sx={{
					mt: '3px', fontSize: '13px', fontWeight: 650, color: L.muted, fontFamily: L.font,
					whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
				}}>
					{subtitle}
				</Typography>
			</Box>
		)
	}

	// Mobile — full-screen takeover per the "Mobile · appointment details" design.
	// View mode renders MobileAppointmentDetails; edit mode reuses the mobile
	// add-appointment components (which bring their own sticky footers).
	const renderMobileDrawer = () => {
		const hoursUntilStart = (new Date(appointment.start_ts).getTime() - Date.now()) / (1000 * 60 * 60)
		const withinCancelWindow = Boolean(canChargeFee) && Boolean(merchant?.late_cancel_hours) && hoursUntilStart < merchant.late_cancel_hours

		return (
			<Drawer
				anchor="bottom"
				open={open}
				onClose={onClose}
				PaperProps={{
					sx: {
						height: '100%',
						maxHeight: '100%',
						borderRadius: 0,
						bgcolor: L.bg
					}
				}}
			>
				<LavMobileContext.Provider value={true}>
					{editMode ? (
						<Box sx={{
							position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0,
							bgcolor: L.bg, fontFamily: L.font,
						}}>
							{renderMobileEditHeader()}
							<Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
								{renderMobileContent()}
							</Box>
							{editSlotOpen && renderEditSlotPicker(true)}
							{renderEditMoveConfirm(true)}
						</Box>
					) : (
						<Box sx={{ position: 'relative', height: '100%', minHeight: 0 }}>
						<MobileAppointmentDetails
							onReschedule={openReschedule}
							moved={moved}
							onOpenClientProfile={openClientProfile}
							onOpenPetProfile={openPetProfile}
							appointment={appointment}
							client={client}
							pet={pet}
							service={service}
							groupedAppointments={groupedAppointments}
							merchant={merchant}
							status={currentStatus}
							paidRecord={paymentDetails}
							loading={loading}
							updatingStatus={updatingStatus}
							formatCurrency={formatCurrency}
							withinCancelWindow={withinCancelWindow}
							canChargeFee={Boolean(canChargeFee)}
							onClose={onClose}
							onEdit={handleEnterEditMode}
							enteringEdit={enteringEdit}
							onCheckout={handleCheckOut}
							onUndo={handleUndoStatus}
							onNoShow={performNoShow}
							onCancelNoFee={handleCancelWithoutFee}
							onCancelWithFee={handleCancelWithFee}
							onViewInvoice={viewInvoice}
							onDownloadInvoice={downloadInvoice}
							downloadingInvoice={downloadingInvoice}
						/>
						{rescheduleOpen && renderRescheduleFlow(true)}
						{renderToast()}
						</Box>
					)}
				</LavMobileContext.Provider>
			</Drawer>
		)
	}

	// More Options Menu (for both mobile and desktop view mode)
	const renderMoreOptionsMenu = () => (
		<Menu
			anchorEl={moreMenuAnchor}
			open={Boolean(moreMenuAnchor)}
			onClose={() => setMoreMenuAnchor(null)}
			PaperProps={{
				sx: {
					mt: 1,
					boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
					border: '1px solid #E2E8F0',
					borderRadius: '8px',
					minWidth: '200px'
				}
			}}
		>
			<MenuItem
				onClick={() => {
					setMoreMenuAnchor(null)
					handleEnterEditMode()
				}}
				sx={{
					px: '16px',
					py: '12px',
					'&:hover': {
						bgcolor: '#F7FAFC'
					}
				}}
			>
				<ListItemIcon>
					<EditIcon sx={{ fontSize: 20, color: '#9F7AEA' }} />
				</ListItemIcon>
				<ListItemText
					primary="Edit"
					primaryTypographyProps={{
						fontSize: '14px',
						fontWeight: 400,
						color: '#2D3748'
					}}
				/>
			</MenuItem>
			<MenuItem
				onClick={() => {
					setMoreMenuAnchor(null)
					handleCancelAppointment()
				}}
				disabled={updatingStatus || currentStatus === 'cancelled'}
				sx={{
					px: '16px',
					py: '12px',
					'&:hover': {
						bgcolor: '#F7FAFC'
					}
				}}
			>
				<ListItemIcon>
					<CancelIcon sx={{ fontSize: 20, color: '#EF4444' }} />
				</ListItemIcon>
				<ListItemText
					primary="Cancel Appointment"
					primaryTypographyProps={{
						fontSize: '14px',
						fontWeight: 400,
						color: '#2D3748'
					}}
				/>
			</MenuItem>
			<MenuItem
				onClick={() => {
					setMoreMenuAnchor(null)
					handleNoShow()
				}}
				disabled={updatingStatus || currentStatus === 'noShow'}
				sx={{
					px: '16px',
					py: '12px',
					'&:hover': {
						bgcolor: '#F7FAFC'
					}
				}}
			>
				<ListItemIcon>
					<NoShowIcon sx={{ fontSize: 20, color: '#F59E0B' }} />
				</ListItemIcon>
				<ListItemText
					primary="Mark No Show"
					primaryTypographyProps={{
						fontSize: '14px',
						fontWeight: 400,
						color: '#2D3748'
					}}
				/>
			</MenuItem>
		</Menu>
	)

	// Desktop right-side panel. View mode renders the Claude Design
	// "Appointment details" panel (DesktopAppointmentDetails, sharing its body
	// with the mobile view); edit mode keeps the legacy add-appointment chrome.
	const renderDesktopDialog = () => {
		const hoursUntilStart = (new Date(appointment.start_ts).getTime() - Date.now()) / (1000 * 60 * 60)
		const desktopWithinCancelWindow = Boolean(canChargeFee) && Boolean(merchant?.late_cancel_hours) && hoursUntilStart < merchant.late_cancel_hours

		return (
		<Drawer
			anchor="right"
			open={open}
			onClose={onClose}
			PaperProps={{
				sx: {
					width: '384px',
					bgcolor: editMode ? '#FFFFFF' : L.bg,
					boxShadow: '0px 4px 4px -4px rgba(12, 12, 13, 0.05), 0px 16px 32px -4px rgba(12, 12, 13, 0.1)',
					borderRadius: '12px 0 0 12px'
				}
			}}
		>
			{editMode ? (
				<Box sx={{ position: 'relative', height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
					{/* Edit-mode header */}
					<Box sx={{
						display: 'flex',
						alignItems: 'center',
						p: '16px',
						borderBottom: '1px solid #E2E8F0'
					}}>
						<Typography sx={{
							fontWeight: 400,
							fontSize: '22px',
							lineHeight: '1.273em',
							color: '#2D3748'
						}}>
							{editModeStep === 'single'
								? (editingAppointmentIndex !== null ? 'Edit Pet' : 'Add Pet')
								: 'Edit Appointment'}
						</Typography>
					</Box>

					{/* Content Container */}
					<Box sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: '18px',
						p: '16px',
						flexGrow: 1,
						overflow: 'auto'
					}}>
						{renderMobileContent()}
					</Box>
					{editSlotOpen && renderEditSlotPicker(false)}
					{renderEditMoveConfirm(false)}
				</Box>
			) : (
				<Box sx={{ position: 'relative', height: '100%', minHeight: 0, overflow: 'hidden' }}>
				<DesktopAppointmentDetails
					onReschedule={openReschedule}
					moved={moved}
					onOpenClientProfile={openClientProfile}
					onOpenPetProfile={openPetProfile}
					appointment={appointment}
					client={client}
					pet={pet}
					service={service}
					groupedAppointments={groupedAppointments}
					merchant={merchant}
					status={currentStatus}
					paidRecord={paymentDetails}
					loading={loading}
					updatingStatus={updatingStatus}
					formatCurrency={formatCurrency}
					withinCancelWindow={desktopWithinCancelWindow}
					canChargeFee={Boolean(canChargeFee)}
					onClose={onClose}
					onEdit={handleEnterEditMode}
					enteringEdit={enteringEdit}
					onCheckout={handleCheckOut}
					onUndo={handleUndoStatus}
					onNoShow={performNoShow}
					onCancelNoFee={handleCancelWithoutFee}
					onCancelWithFee={handleCancelWithFee}
					onViewInvoice={viewInvoice}
					onDownloadInvoice={downloadInvoice}
					downloadingInvoice={downloadingInvoice}
				/>
				{rescheduleOpen && renderRescheduleFlow(false)}
				{renderToast()}
				</Box>
			)}
		</Drawer>
	)
	}

	return (
		<>
			{/* Render Mobile Drawer or Desktop Dialog */}
			{isMobile ? renderMobileDrawer() : renderDesktopDialog()}

			{/* More Options Menu (for both mobile and desktop view mode) */}
			{!editMode && renderMoreOptionsMenu()}

			{/* Edit client from the reschedule confirm step (stays on top of the
			    picker / confirm; the pending slot is untouched). */}
			<AddEditClientDialog
				open={editClientOpen}
				onClose={() => setEditClientOpen(false)}
				merchant={merchant}
				editingClient={client}
				onClientSaved={({ client: updated }) => {
					if (updated) setClient(prev => ({ ...(prev || {}), ...updated }))
					setEditClientOpen(false)
				}}
			/>

			{/* Cancel Choice Dialog (shared for both mobile and desktop) */}
			<Dialog
				open={showCancelChoiceDialog}
				onClose={handleCloseCancelDialog}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<CancelIcon color="error" />
						<Typography variant="h6" component="span">
							Cancel Appointment
						</Typography>
					</Box>
				</DialogTitle>

				<DialogContent>
					{isWithinCancelWindow ? (
						<Stack spacing={2} sx={{ mt: 1 }}>
							<Typography variant="body1" color="text.primary">
								This cancellation is within the late cancellation window
								({merchant?.late_cancel_hours} hours before appointment).
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Please choose how to proceed:
							</Typography>

							<Box sx={{
								p: 2,
								bgcolor: 'warning.lighter',
								borderRadius: 1,
								border: '1px solid',
								borderColor: 'warning.light'
							}}>
								<Typography variant="body2" fontWeight="medium">
									⚠️ Canceling with a fee will charge the customer according to your configured cancellation policy.
								</Typography>
							</Box>
						</Stack>
					) : (
						<Typography variant="body1" color="text.primary" sx={{ mt: 1 }}>
							Are you sure you want to cancel this appointment?
						</Typography>
					)}
				</DialogContent>

				<DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
					<Button
						onClick={handleCloseCancelDialog}
						variant="outlined"
						color="inherit"
					>
						Go Back
					</Button>

					<Box sx={{ flex: 1 }} />

					{isWithinCancelWindow ? (
						<>
							<Button
								onClick={handleConfirmCancelWithoutFee}
								variant="outlined"
								color="primary"
								disabled={updatingStatus}
							>
								Cancel Without Fee
							</Button>
							<Button
								onClick={handleConfirmCancelWithFee}
								variant="contained"
								color="error"
								disabled={updatingStatus}
								startIcon={updatingStatus ? <CircularProgress size={20} /> : <CancelIcon />}
							>
								{updatingStatus ? 'Processing...' : 'Cancel With Fee'}
							</Button>
						</>
					) : (
						<Button
							onClick={handleConfirmCancelWithoutFee}
							variant="contained"
							color="error"
							disabled={updatingStatus}
							startIcon={updatingStatus ? <CircularProgress size={20} /> : <CancelIcon />}
						>
							{updatingStatus ? 'Cancelling...' : 'Confirm Cancel'}
						</Button>
					)}
				</DialogActions>
			</Dialog>

			{/* Payment Dialog (shared for both mobile and desktop) */}
			<LogPaymentDialog
				open={showPaymentDialog}
				onClose={() => setShowPaymentDialog(false)}
				appointment={appointment}
				merchant={merchant}
				onPaymentLogged={handlePaymentLogged}
			/>

			{/* Payment Details Dialog (shared for both mobile and desktop) */}
			<Dialog
				open={showPaymentDetails}
				onClose={() => setShowPaymentDetails(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<CheckOutIcon color="success" />
						<Typography variant="h6" component="span">
							Payment Details
						</Typography>
					</Box>
				</DialogTitle>

				<DialogContent>
					{loadingPayment ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
							<CircularProgress />
						</Box>
					) : paymentDetails ? (
						<Stack spacing={2} sx={{ mt: 1 }}>
							{/* Payment Method */}
							<Box>
								<Typography variant="subtitle2" color="text.secondary" gutterBottom>
									Payment Method
								</Typography>
								<Typography variant="body1" fontWeight="medium">
									{paymentDetails.payment_method === 'cash' ? 'Cash' :
										paymentDetails.payment_method === 'card_terminal' ? 'Card' :
											paymentDetails.payment_method === 'check' ? 'Check' :
												paymentDetails.payment_method.charAt(0).toUpperCase() + paymentDetails.payment_method.slice(1)}
								</Typography>
							</Box>

							<Divider />

							{/* Amount Breakdown */}
							<Box>
								<Typography variant="subtitle2" color="text.secondary" gutterBottom>
									Amount Breakdown
								</Typography>
								<Stack spacing={1}>
									<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
										<Typography variant="body2">Service:</Typography>
										<Typography variant="body2">{formatCurrency(paymentDetails.service_amount)}</Typography>
									</Box>

									{paymentDetails.add_ons && paymentDetails.add_ons.length > 0 && (
										<>
											{paymentDetails.add_ons.map((addon, index) => (
												<Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', pl: 2 }}>
													<Typography variant="body2" color="text.secondary">+ {addon.name}</Typography>
													<Typography variant="body2" color="text.secondary">{formatCurrency(addon.amount)}</Typography>
												</Box>
											))}
										</>
									)}

									<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
										<Typography variant="body2">Tax:</Typography>
										<Typography variant="body2">{formatCurrency(paymentDetails.tax_amount)}</Typography>
									</Box>

									{paymentDetails.tips_amount > 0 && (
										<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
											<Typography variant="body2">Tips:</Typography>
											<Typography variant="body2">{formatCurrency(paymentDetails.tips_amount)}</Typography>
										</Box>
									)}

									<Divider />

									<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
										<Typography variant="h6" fontWeight="bold">Total:</Typography>
										<Typography variant="h6" fontWeight="bold" color="primary">
											{formatCurrency(paymentDetails.total_amount)}
										</Typography>
									</Box>
								</Stack>
							</Box>

							{paymentDetails.notes && (
								<>
									<Divider />
									<Box>
										<Typography variant="subtitle2" color="text.secondary" gutterBottom>
											Notes
										</Typography>
										<Typography variant="body2" sx={{
											whiteSpace: 'pre-wrap',
											backgroundColor: 'action.hover',
											p: 2,
											borderRadius: 1
										}}>
											{paymentDetails.notes}
										</Typography>
									</Box>
								</>
							)}

							<Divider />

							{/* Payment Date */}
							<Box>
								<Typography variant="subtitle2" color="text.secondary" gutterBottom>
									Payment Date
								</Typography>
								<Typography variant="body2">
									{new Date(paymentDetails.created_at).toLocaleString()}
								</Typography>
							</Box>

							{/* Payment Status */}
							<Box>
								<Typography variant="subtitle2" color="text.secondary" gutterBottom>
									Status
								</Typography>
								<Typography variant="body2" sx={{
									color: paymentDetails.payment_status === 'completed' ? 'success.main' : 'text.primary',
									fontWeight: 'medium'
								}}>
									{paymentDetails.payment_status.charAt(0).toUpperCase() + paymentDetails.payment_status.slice(1)}
								</Typography>
							</Box>
						</Stack>
					) : (
						<Box sx={{ textAlign: 'center', py: 4 }}>
							<Typography variant="body1" color="text.secondary">
								No payment record found for this appointment.
							</Typography>
						</Box>
					)}
				</DialogContent>

				<DialogActions>
					<Button onClick={() => setShowPaymentDetails(false)} variant="outlined">
						Close
					</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}

export default AppointmentDetailsDialog
